import * as React from "react";
import type { Environment } from "../types";

/**
 * S3(CDN)에 올라간 관심사별 설정 JSON(endpoints.json / apps.json 등)을
 * 가져오는 공용 레이어. CDN JSON이 단일 소스다 — 하드코딩 폴백 없음.
 *
 * - 성공 응답은 파일당 세션 내 1회만 fetch(메모이즈). 실패는 캐시하지
 *   않으므로 다음 호출(react-query retry 등)에서 재시도된다.
 * - fetch 실패 시 `getApiBase`는 throw, `useRemoteConfigValue`는 undefined —
 *   호출부는 로딩/에러 상태로 처리한다.
 * - env별 값은 app-launcher-v2.json 컨벤션을 따른다:
 *   값이 env별로 다르면 `{ dev, stage, production }` 객체, 동일하면 문자열.
 */

/** 설정 JSON이 올라가는 CDN 베이스. dev/stage는 staging CDN을 공유한다. */
const REMOTE_CONFIG_BASES: Record<Environment, string> = {
  dev: "https://stg-contents.crosstoken.io/frontend/common/app-launcher",
  stage: "https://stg-contents.crosstoken.io/frontend/common/app-launcher",
  production: "https://contents.crosstoken.io/frontend/common/app-launcher",
};

const FETCH_TIMEOUT_MS = 3_000;

/** env별로 다르면 객체, 모든 env 동일하면 T 단일 값. */
export type RemoteEnvValue<T> = T | Record<Environment, T>;

export function resolveEnvValue<T>(
  value: RemoteEnvValue<T> | null | undefined,
  env: Environment,
): T | undefined {
  if (value == null) return undefined;
  if (
    typeof value === "object" &&
    !Array.isArray(value) &&
    ("dev" in value || "stage" in value || "production" in value)
  ) {
    return (value as Record<Environment, T>)[env];
  }
  return value as T;
}

const configCache = new Map<string, Promise<unknown>>();

/**
 * `<CDN base>/<file>`을 fetch해 JSON으로 반환. 실패 시 null.
 * 성공 결과만 URL 단위로 메모이즈한다 — 실패는 캐시하지 않으므로
 * 다음 호출에서 재시도된다.
 */
export function fetchRemoteConfig<T>(
  file: string,
  env: Environment,
): Promise<T | null> {
  const url = `${REMOTE_CONFIG_BASES[env]}/${file}`;
  let cached = configCache.get(url);
  if (!cached) {
    cached = (async () => {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
      try {
        const res = await fetch(url, {
          cache: "no-store",
          signal: controller.signal,
        });
        if (!res.ok) return null;
        return (await res.json()) as unknown;
      } catch {
        return null;
      } finally {
        clearTimeout(timer);
      }
    })().then((result) => {
      if (result === null) configCache.delete(url);
      return result;
    });
    configCache.set(url, cached);
  }
  return cached as Promise<T | null>;
}

/** 테스트 전용 — fetch 메모이즈 캐시 초기화. */
export function __clearRemoteConfigCacheForTest(): void {
  configCache.clear();
}

// ---------------------------------------------------------------------------
// endpoints.json — 백엔드 API base URL
// ---------------------------------------------------------------------------

export type RemoteApiName =
  | "walletServer"
  | "bondingCurve"
  | "posa"
  | "gameReward"
  | "gameSwap";

type EndpointsConfig = {
  apis?: Partial<Record<RemoteApiName, RemoteEnvValue<string>>>;
};

function isSafeBaseUrl(value: unknown): value is string {
  return typeof value === "string" && value.startsWith("https://");
}

/**
 * endpoints.json의 API base를 반환한다. CDN 값이 단일 소스 — fetch 실패
 * 또는 키 누락/비https 값이면 throw하고, react-query retry로 재시도된다.
 * 쿼리 훅의 queryFn 안에서 `await getApiBase("posa", env)` 형태로 사용한다.
 */
export async function getApiBase(
  name: RemoteApiName,
  env: Environment,
): Promise<string> {
  const config = await fetchRemoteConfig<EndpointsConfig>(
    "endpoints.json",
    env,
  );
  const remote = resolveEnvValue(config?.apis?.[name], env);
  if (!isSafeBaseUrl(remote)) {
    throw new Error(
      `[remote-config] endpoints.json missing or invalid api base: ${name} (${env})`,
    );
  }
  return remote;
}

// ---------------------------------------------------------------------------
// apps.json — 앱/서비스 링크 (app-launcher JSON에 없는 것만)
// ---------------------------------------------------------------------------

export type AppsConfig = {
  skills?: RemoteEnvValue<string>;
  gametokenBridge?: RemoteEnvValue<string>;
  /** 브릿지 버튼을 새창 대신 같은 창으로 여는 gametoken 사이트 URL prefix 목록. */
  gametokenSites?: string[];
  bridgePoweredBy?: { url?: RemoteEnvValue<string> };
};

/**
 * 컴포넌트에서 원격 설정 값 하나를 구독하는 최소 훅.
 * react-query 의존 없이 동작한다 (QueryClientProvider 없는 소비자도 안전).
 * 로딩 중/실패 시 undefined — 호출부에서 `?? 폴백`으로 처리.
 */
export function useRemoteConfigValue<TConfig, TValue>(
  file: string,
  env: Environment,
  select: (config: TConfig | null) => TValue | undefined,
): TValue | undefined {
  const [value, setValue] = React.useState<TValue | undefined>(undefined);
  const selectRef = React.useRef(select);
  selectRef.current = select;

  React.useEffect(() => {
    let alive = true;
    fetchRemoteConfig<TConfig>(file, env).then((config) => {
      if (!alive) return;
      const next = selectRef.current(config);
      if (next !== undefined) setValue(next);
    });
    return () => {
      alive = false;
    };
  }, [file, env]);

  return value;
}
